import { Injectable } from '@angular/core';
import { ProductId } from '@app/core/enums/product-id';
import {
    CancelFutureModifiablePremiumDeterminingFactorPropsInterface,
    NonRegularDriverPropsInterface, PolicyProductInterface,
    PurchasedProductAccidentInterface, PurchasedProductBicycleInterface,
    PurchasedProductCarInterface, PurchasedProductHomeInterface,
    PurchasedProductLegalCounselInterface, PurchasedProductLiabilityInterface,
    PurchasedProductMopedInterface, PurchasedProductMotorInterface,
    PurchasedProductPayloadInterface, PurchasedProductPermittedCoverageInterface,
    PurchasedProductPetInterface,
    PurchasedProductTravelInterface,
    PurchasedProductUnion,
    Response,
} from '@app/core/interfaces';
import { PurchasedProductCaravanInterface } from '@app/core/interfaces/product/purchased-product/purchased-product-caravan.interface';
import { RequestService as RequestService2 } from '@app/core/request/request.service';
import { DateService, RequestService } from 'outshared-lib';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

/** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
@Injectable({
    providedIn: 'root',
})
export class ProductService {

    constructor(
        private requestService: RequestService,
        private requestService2: RequestService2,
    ) {}

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public loadPolicy(props: { present_history_indication: boolean }): Observable<PolicyProductInterface[]> {
        const payload = {
            present_history_indication: props.present_history_indication ? 'J' : 'N',
            premium_determining_factor_indication: null,
        };

        return this.requestService.post('en-gb/relation-general/request/policy', payload).pipe(
            map((response) => (response.data.purchased_products ?? [])),
            map((products) => products.map((product) => this.validateStatusCode(product))),
            map((products) => products.map((product) => this.validatePermittedCoverage(product))),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public loadPurchasedProduct(
        props: {
            purchased_product_sequence_number: number,
            product_id: ProductId,
        },
    ): Observable<PurchasedProductUnion> {
        const { purchased_product_sequence_number, product_id } = props;
        const payload: PurchasedProductPayloadInterface = {
            purchased_product: {
                purchased_product_sequence_number,
            },
        };

        return this.getPurchasedProduct(product_id, payload).pipe(
            map((purchased_product) => this.validateStatusCode(purchased_product)),
            map((purchased_product) => this.validatePermittedCoverage(purchased_product)),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public cancelFuturePurchasedProduct(props: { purchased_product_sequence_number: number }): Observable<Response> {
        const { purchased_product_sequence_number } = props;
        const payload = {
            purchased_product: {
                purchased_product_sequence_number,
            },
        };

        return this.requestService.post('en-gb/insurance-product/cancel/future-purchased_product', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public modifyFuturePurchasedProductStartDate(
        props: { start_date: string, purchased_product_sequence_number: number },
    ): Observable<Response> {
        const { start_date, purchased_product_sequence_number } = props;
        const payload = {
            purchased_product: {
                start_date,
                purchased_product_sequence_number,
            },
        };

        return this.requestService.post('en-gb/insurance-product/modify/future-purchased_product-start_date', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public terminateActivePurchasedProduct(props: { end_date: string, purchased_product_sequence_number: number }): Observable<Response> {
        const { end_date, purchased_product_sequence_number } = props;
        const payload = {
            purchased_product: {
                end_date,
                purchased_product_sequence_number,
            },
        };

        return this.requestService.post('en-gb/insurance-product/terminate/active-purchased_product', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public reviveTerminatedPurchasedProduct(props: { purchased_product_sequence_number: number }): Observable<Response> {
        const { purchased_product_sequence_number } = props;
        const payload = {
            purchased_product: {
                purchased_product_sequence_number,
            },
        };

        return this.requestService.post('en-gb/insurance-product/revive/terminated-purchased_product', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public modifyTerminationDateForProductEndDate(
        props: {
            purchased_product_sequence_number: number,
            end_date: string,
        }): Observable<Response> {
        const { purchased_product_sequence_number, end_date } = props;
        const payload = {
            purchased_product: {
                end_date,
                purchased_product_sequence_number,
            },
        };

        return this.requestService
            .post('en-gb/insurance-product/modify/new_termination_date_for_future-purchased_product-end_date', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public storeNonRegularDriver(props: {
        purchased_product_sequence_number: number,
        non_regular_driver: NonRegularDriverPropsInterface,
    }): Observable<Response> {
        const { purchased_product_sequence_number, non_regular_driver } = props;
        const payload = {
            afgenomen_product: {
                afgenomen_product_volgnummer: purchased_product_sequence_number,
            },
            afwijkende_bestuurder: {
                achternaam: non_regular_driver.surname,
                geboortedatum: non_regular_driver.birthdate,
                geslacht_code: non_regular_driver.gender_code,
                ingangsdatum: non_regular_driver.start_date,
                relatie_code: non_regular_driver.relation_code,
                tussenvoegsels: non_regular_driver.middle_name_initials,
                voorletters: non_regular_driver.initials,
            },
        };

        // TODO: WINS-5173 - Endpoint aanpassen naar engelse endpoint
        // return this.requestService.post('en-gb/insurance-product/store/non_regular_driver', payload);
        return this.requestService2.post('verzekering-product/vastleggen/afwijkendebestuurder', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public cancelFutureNonRegularDriver(props: { purchased_product_sequence_number: number }): Observable<Response> {
        const { purchased_product_sequence_number } = props;
        const payload = {
            purchased_product: {
                purchased_product_sequence_number,
            },
        };

        return this.requestService.post('en-gb/insurance-product/cancel/future-non_regular_driver', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public terminateActiveNonRegularDriver(props: { end_date: string, purchased_product_sequence_number: number }): Observable<Response> {
        const { end_date, purchased_product_sequence_number } = props;
        const payload = {
            non_regular_driver: {
                end_date,
                purchased_product_sequence_number,
            },
        };

        return this.requestService.post('en-gb/insurance-product/terminate/active-non_regular_driver', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    public cancelFutureModifiablePremiumDeterminingFactor(
        props: CancelFutureModifiablePremiumDeterminingFactorPropsInterface,
    ): Observable<Response> {
        const { purchased_product_sequence_number, premium_determining_factor_id } = props;
        const payload = {
            modifiable_premium_determining_factor: {
                purchased_product_sequence_number,
                premium_determining_factor_id,
            },
        };

        return this.requestService.post('en-gb/insurance-product/cancel/future-modifiable_premium_determining_factor', payload);
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProduct(product_id: ProductId, payload: PurchasedProductPayloadInterface): Observable<PurchasedProductUnion> {
        switch (product_id) {
            case ProductId.liability:
            return this.getPurchasedProductLiability(payload);
            case ProductId.car:
            return this.getPurchasedProductCar(payload);
            case ProductId.moped:
            return this.getPurchasedProductMoped(payload);
            case ProductId.pet:
            return this.getPurchasedProductPet(payload);
            case ProductId.motorcycle:
            return this.getPurchasedProductMotor(payload);
            case ProductId.accidents:
            return this.getPurchasedProductAccident(payload);
            case ProductId.legal:
            return this.getPurchasedProductLegalCounsel(payload);
            case ProductId.travel:
            return this.getPurchasedProductTravel(payload);
            case ProductId.home:
            return this.getPurchasedProductHome(payload);
            case ProductId.bicycle:
            return this.getPurchasedProductBycicle(payload);
            case ProductId.caravan:
            return this.getPurchasedProductCaravan(payload);
        }
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductLiability(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductLiabilityInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-liability', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductCar(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductCarInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-auto', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductMoped(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductMopedInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-limited_speed_motorcycle', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductPet(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductPetInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-pet', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductMotor(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductMotorInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-motorcycle', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductAccident(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductAccidentInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-accidents', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductLegalCounsel(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductLegalCounselInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-legal_assistance', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductTravel(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductTravelInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-travel', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductHome(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductHomeInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-home', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductBycicle(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductBicycleInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-bicycle', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private getPurchasedProductCaravan(payload: PurchasedProductPayloadInterface): Observable<PurchasedProductCaravanInterface> {
        return this.requestService.post('en-gb/insurance-product/request/purchased_product-caravan', payload).pipe(
            map((response) => response.data),
        );
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private validateStatusCode(purchased_product: PurchasedProductUnion): PurchasedProductUnion | null {
        if (!purchased_product) {
            return null;
        }

        if (purchased_product.validity_status_code === 'TOEK' && purchased_product.start_date === '9999-12-31') {
            purchased_product.validity_status_code = 'OSTP';
        }

        return purchased_product;
    }

    /** @deprecated use outshared-lib - InsurancePolicyActionService / InsurancePolicyDataService */
    private validatePermittedCoverage(purchased_product: PurchasedProductUnion): PurchasedProductUnion | null {
        if (!purchased_product) {
            return null;
        }

        const dateService = new DateService();
        const permitted_coverage: PurchasedProductPermittedCoverageInterface[] =
            purchased_product['permitted_coverage'] ? purchased_product['permitted_coverage'] : null;

        if (!permitted_coverage) {
            return purchased_product;
        }

        if (purchased_product.validity_status_code !== 'ACTI') {
            return purchased_product;
        }

        for (const coverage of permitted_coverage) {
            if (coverage.purchased_coverage && coverage.purchased_coverage.end_date) {
                if (dateService.isAfter(coverage.purchased_coverage.end_date, purchased_product.start_date)) {
                    purchased_product.validity_status_code = 'ACWI';
                }
            }
            if (coverage.purchased_coverage_future) {
                if (dateService.isAfter(coverage.purchased_coverage_future.start_date, purchased_product.start_date)) {
                    purchased_product.validity_status_code = 'ACWI';
                }
            }
        }

        return purchased_product;
    }
}
